import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ModuleRegistry } from '@ag-grid-community/core';
import type {
  CellClickedEvent,
  ColDef,
  ColumnMovedEvent,
} from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model';
import type { AgGridReact } from '@ag-grid-community/react';
import { SchemaTypes } from '@axiom/types';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';

import { RawCheckbox } from '../../form/Checkbox/RawCheckbox';
import { AttrsHelper } from '../../../sb-helpers/attrs-helper';

import { DataGridApiRegistry } from './data-grid-api-registry';
import { DataGridNoResults } from './DataGridNoResults';
import { DataGridTooltip } from './DataGridTooltip';
import type { DataGridHeaderProps } from './DataGridHeader';
import { RawDataGridHeader } from './RawDataGridHeader';
import type {
  CellRenderProps,
  DataGridOnCheckedRowsArg,
  DataGridProps,
  InternalDataGridProps,
  SortStructure,
} from './data-grid.types';
import { DataGridInfinite } from './DataGridInfinite';
import { DataGridPagination } from './DataGridPagination';
import { useDataGridLicenseContext } from './DataGridLicenseProvider';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';

ModuleRegistry.registerModules([
  MenuModule,
  ClipboardModule,
  ClientSideRowModelModule,
  InfiniteRowModelModule,
]);

type DataGridChild = React.ReactElement<DataGridHeaderProps<unknown>>;

// Field/col-id for the checkbox column DataGrid injects when
// onCheckboxGroupChanged is provided. It is not a user-defined DataGridHeader,
// so clicking it must never trigger row-click navigation.
const CHECKBOX_COLUMN_FIELD = 'column-checkbox';

// Stable default so formatColumns (which depends on rowsChecked) does not
// recompute new columnDefs on every render — that churn re-emits columnDefs to
// AgGridReact and wipes the no-rows overlay (MIE-317).
const EMPTY_ROWS_CHECKED: DataGridOnCheckedRowsArg = {
  allRowsChecked: false,
  checkedRows: [],
};

export const DataGrid = <AsyncConnectorResult,>({
  calculateRowBadge,
  children,
  dataSource,
  displayMode,
  name,
  noResultsMessage = 'No Results Found',
  onColumnOrderChanged,
  onSelectionChanged,
  onCheckboxGroupChanged,
  getContextMenuItems,
  rowsChecked = EMPTY_ROWS_CHECKED,
  contained,
  hidePagination,
  lockColumnPinning,
  relaxed,
  simple,
  suppressRowClickForColumns,
  allowContextMenu,
  tooltipShowDelay = 2000,
  tooltipShowMode = 'whenTruncated',
  tooltipComponent = DataGridTooltip,
}: DataGridProps<AsyncConnectorResult>) => {
  const defaultSort = useMemo(() => {
    const sort: SortStructure = {};
    const typedChildren = React.Children.toArray(children) as DataGridChild[];
    typedChildren.forEach(child => {
      if (child.props.initialSort) {
        sort[child.props.name] = child.props.initialSort;
      }
    });
    return sort;
  }, [children]);
  const gridRef = useRef<AgGridReact>(null);

  /*
    So a test can reach this grid's api by name - see `data-grid-api-registry`. Nothing in the app
    reads it; a reader's own gestures go through ag-grid directly.
  */
  useEffect(() => {
    // An unnamed grid cannot be asked for by name, so there is nothing to register it under.
    if (!name) return;

    DataGridApiRegistry.register({ name, ref: gridRef });

    return () => {
      DataGridApiRegistry.unregister({ name });
    };
  }, [name]);

  /** Every row the grid currently holds - the infinite model leaves un-fetched nodes without data. */
  const collectRowData = () => {
    const rowData: Array<Record<string, unknown>> = [];
    gridRef.current?.api.forEachNode(
      node => node.data && rowData.push(node.data)
    );

    return rowData;
  };

  const isActivated = useDataGridLicenseContext();
  const [sorting, setSorting] = useState<SortStructure>(defaultSort);
  // Tracks the column of the most recent cell click, read by
  // formattedOnSelectionChange below to decide whether to suppress it.
  //
  // Deliberately not `api.getFocusedCell()`: AG-Grid only populates that from
  // its own mousedown-or-touchstart listener, chosen by feature-detecting
  // `'ontouchstart' in HTMLElement`. Environments (including jsdom) that
  // report touch support but only ever dispatch mouse events leave AG-Grid
  // listening for a touchstart that never comes, so getFocusedCell() stays
  // null forever. `cellClicked` reflects the actual click target directly,
  // unaffected by that split.
  const lastClickedColDef = useRef<ColDef>();
  const handleCellClicked = useCallback((event: CellClickedEvent) => {
    lastClickedColDef.current = event.column.getColDef();
  }, []);

  const isLicensedForContextMenu = !getContextMenuItems || isActivated;

  if (!isLicensedForContextMenu) {
    window.console.warn(
      'DataGrid ContextMenus are tied behind a license. We only have that license for AHQ. Talk to UI Dev council on how to rectify.'
    );
  }

  const licensedContextMenuItems = isLicensedForContextMenu
    ? getContextMenuItems
    : undefined;

  const formatColumns = useMemo<Array<Record<string, unknown>>>(() => {
    const typedChildren = React.Children.toArray(children) as DataGridChild[];
    const formattedColumns: Array<Record<string, unknown>> = typedChildren.map(
      (c: DataGridChild) => {
        const childProps = c.props;

        return {
          cellRenderer: childProps.cellRender
            ? ({ data, node, column, value, api }: CellRenderProps) => {
                if (data && typeof childProps.cellRender === 'function') {
                  return childProps.cellRender({
                    data,
                    node,
                    column,
                    value,
                    api,
                  });
                }

                return (
                  <div data-test="grid-fetching-data">Fetching Data...</div>
                );
              }
            : null,
          /*
            Unwrapped here rather than handed ag-grid's event straight through: the prop types its
            argument as the row, and the row is on the event's `data`.
          */
          onCellClicked: childProps.onCellClicked
            ? ({ data }: { data: Record<string, unknown> }) =>
                childProps.onCellClicked?.(data)
            : undefined,
          field: childProps.name,
          valueGetter: childProps.valueGetter,
          /*
            **Never a function.** ag-grid shows `headerName` raw wherever it needs the column as
            text - the ghost it drags, the column menu - and `displayName` may be a function, for a
            header that is a component rather than a heading. Handed one of those, ag-grid printed
            `[object Object]`. Such a header has no text to offer, so it offers none; the component
            itself still renders through `headerComponentParams.displayName`.

            **`undefined` is passed on as it is**, and is not the same as `''`: left undefined,
            ag-grid titles the column from its field, which `DataGridHeader.test` pins.
          */
          headerName:
            typeof childProps.displayName === 'function'
              ? ''
              : childProps.displayName,
          headerComponent: RawDataGridHeader,
          suppressHeaderContextMenu: true,
          suppressHeaderMenuButton: true,
          headerComponentParams: {
            name: childProps.name,
            displayName: childProps.displayName,
            initialSort: childProps.initialSort,
            sortingOrder: childProps.sortingOrder,
            pinned: childProps.pinned,
            lockPinned: childProps.lockPinned,
            width: childProps.width,
            stretched: childProps.stretched,
            cellRender: childProps.cellRender,
            FiltersRender: childProps.FiltersRender,
            onCellClicked: childProps.onCellClicked,
            onSortChange: (sortChange: SortStructure) => {
              const hasActiveSort = Object.values(sortChange).some(Boolean);
              setSorting(hasActiveSort ? sortChange : defaultSort);
            },
            sorting,
          },
          sortable: !!childProps.sortingOrder,
          sortingOrder: childProps.sortingOrder,
          pinned: childProps.pinned,
          lockPinned: childProps.lockPinned ?? lockColumnPinning,
          suppressMovable: childProps.suppressMovable,
          width: childProps.width,
          flex: childProps.stretched,
          /*
            Both undefined on a column that did not ask, and that is what leaves `defaultColDef`
            in charge of it: ag-grid merges a colDef over the defaults with `copyUndefined: false`,
            so a key with no answer here is not an override.
          */
          autoHeight: childProps.wrapText,
          wrapText: childProps.wrapText,
          autoHeaderHeight: childProps.wrapHeaderText,
          wrapHeaderText: childProps.wrapHeaderText,
          cellClass: childProps.cellClassName,
          // AG-Grid's `tooltipShowMode="whenTruncated"` only attaches its
          // truncation check to cells WITHOUT a custom cell renderer, so the
          // tooltip is opt-in and only meaningful on text columns (no
          // `cellRender`). Use `valueGetter`/`field` for the cell text and the
          // tooltip reads that same value, so it always matches what's shown.
          tooltipValueGetter: childProps.tooltip
            ? (params: { data?: unknown; value?: string | null }) =>
                childProps.tooltipValueGetter
                  ? childProps.tooltipValueGetter({ data: params.data })
                  : (params.value ?? null)
            : undefined,
          // Custom tooltip component so long text wraps/scrolls inside a capped
          // popup instead of AG-Grid's default tooltip, which has no max-width
          // and renders very wide. Defaults to <DataGridTooltip /> (styled via
          // `.ag-tooltip-axiom` in DataGrid.less); overridable per <DataGrid />,
          // and per column over that.
          tooltipComponent: childProps.tooltip
            ? (childProps.tooltipComponent ?? tooltipComponent)
            : undefined,
        };
      }
    );

    if (onCheckboxGroupChanged) {
      formattedColumns.unshift({
        field: CHECKBOX_COLUMN_FIELD,
        suppressHeaderContextMenu: true,
        suppressHeaderMenuButton: true,
        cellRenderer: ({
          data: cellData,
          node: { rowIndex },
        }: {
          data?: Record<string, unknown>;
          node: { rowIndex: number };
        }) => {
          if (cellData) {
            const hasCheckedRow =
              rowsChecked.allRowsChecked ||
              rowsChecked.checkedRows.includes(cellData);

            return (
              <div className="grid-checkbox">
                <RawCheckbox
                  dataType={SchemaTypes.ZodBoolean}
                  name={`row-check-${rowIndex}`}
                  value={hasCheckedRow}
                  option
                  onChange={value => {
                    let newCheckedRows = [...rowsChecked.checkedRows];
                    if (value) {
                      newCheckedRows.push(cellData);
                    } else {
                      newCheckedRows = newCheckedRows.filter(
                        row => row !== cellData
                      );
                    }

                    onCheckboxGroupChanged({
                      allRowsChecked:
                        collectRowData().length === newCheckedRows.length,
                      checkedRows: newCheckedRows,
                    });
                  }}
                />
              </div>
            );
          }

          return null;
        },
        headerComponent: () => {
          return (
            <div className="grid-checkbox">
              <RawCheckbox
                dataType={SchemaTypes.ZodBoolean}
                name="grid-header-checkbox"
                option
                value={rowsChecked.allRowsChecked}
                onChange={value => {
                  const isChecked = value === true;

                  onCheckboxGroupChanged({
                    allRowsChecked: isChecked,
                    checkedRows: isChecked ? collectRowData() : [],
                  });
                }}
              />
            </div>
          );
        },
        headerComponentParams: {},
        sortable: false,
        pinned: 'left',
        width: 65,
      });
    }

    if (calculateRowBadge) {
      formattedColumns.unshift({
        field: 'row-badge-colors',
        cellStyle: ({ data }: { data?: Record<string, unknown> }) => {
          // Header cell gets called, but will not have data
          if (data) {
            const bgColor = calculateRowBadge(data);
            return { padding: 0, backgroundColor: bgColor };
          }

          return { padding: 0 };
        },
        suppressSizeToFit: true,
        suppressAutoSize: true,
        suppressHeaderContextMenu: true,
        suppressHeaderMenuButton: true,
        resizable: false,
        minWidth: 8,
        sortable: false,
        pinned: 'left',
        width: 8,
      });
    }

    return formattedColumns;
    /*
      **`children` is in here because the column set is a prop.** Without it these definitions are
      built once and a caller that adds, drops or re-renders a column is a silent no-op - the grid
      goes on showing whatever it was first given. ag-grid keeps a column's width and sort across a
      rebuild whose `colId`s still match, so the cost of recomputing is the rebuild itself.
    */
  }, [
    calculateRowBadge,
    children,
    defaultSort,
    lockColumnPinning,
    onCheckboxGroupChanged,
    rowsChecked,
    sorting,
    tooltipComponent,
  ]);

  const noRowsOverlayComponentParams = useMemo(() => {
    return {
      noRowsMessageFunc: () => noResultsMessage,
    };
  }, [noResultsMessage]);

  const GridComponent =
    displayMode === 'infinite' ? DataGridInfinite : DataGridPagination;

  /*
    **Only when the drag is over.** ag-grid fires this for every step of a move, and each one would
    be a state write and a rebuild of every column under the reader's cursor.
  */
  const handleColumnMoved = onColumnOrderChanged
    ? (event: ColumnMovedEvent) => {
        if (!event.finished) return;

        onColumnOrderChanged(
          event.api.getAllDisplayedColumns().map(column => column.getColId())
        );
      }
    : undefined;

  const formattedOnSelectionChange = onSelectionChanged
    ? () => {
        // get the column which was clicked
        const clickedColDef = lastClickedColDef.current;
        const currentColumnName = clickedColDef?.headerComponentParams?.name;

        // the checkbox column is injected by DataGrid (not a user-defined
        // DataGridHeader), so clicking it must never trigger row navigation
        if (clickedColDef?.field === CHECKBOX_COLUMN_FIELD) {
          return;
        }

        // don't fire onSelectionChanged if column is suppressed
        if (suppressRowClickForColumns?.includes(currentColumnName)) {
          return;
        }
        if (gridRef.current) {
          onSelectionChanged(gridRef.current.api.getSelectedRows());
        }
      }
    : () => {};
  return (
    <GridComponent
      ref={gridRef}
      className={AttrsHelper.formatClassname(
        relaxed && 'relax-rows',
        simple && 'simple-rows'
      )}
      contained={contained}
      hidePagination={hidePagination}
      dataSource={dataSource as InternalDataGridProps['dataSource']}
      displayMode={displayMode}
      name={name}
      noResultsComponent={DataGridNoResults}
      getContextMenuItems={licensedContextMenuItems}
      noRowsOverlayComponentParams={noRowsOverlayComponentParams}
      onSelectionChanged={formattedOnSelectionChange}
      onColumnMoved={handleColumnMoved}
      onInternalCellClicked={handleCellClicked}
      columns={formatColumns}
      sorting={sorting}
      allowContextMenu={allowContextMenu}
      tooltipShowDelay={tooltipShowDelay}
      tooltipShowMode={tooltipShowMode}
      // Make tooltips hoverable so long, scrollable content (see
      // DataGridTooltip) can be reached with the mouse — without this AG-Grid
      // gives tooltips `pointer-events: none` and the cursor can't enter them.
      tooltipInteraction
    >
      {children}
    </GridComponent>
  );
};

export { type DataGridProps } from './data-grid.types';
